import { useEffect } from 'react';
import {
  useFeatureFlagEnabled,
  useFeatureFlagVariantKey,
} from '@posthog/react';
import { useFamilyGroupPropertiesReady } from 'src/hooks/useFamilyGroupPropertiesReady';
import { warnOnFlagReadUnderForeignIdentity } from 'src/modules/analytics/posthogClient';
import { hasOwn } from 'src/utils/hasOwn';

export const POSTHOG_BOOLEAN_FLAGS = [
  'cahier_vacances_enabled',
  'device_service_incident_banner_enabled',
  'is_impact_tab_enabled',
  'is_free_first_lesson_enabled',
] as const;

export type PostHogFeatureFlagKey = (typeof POSTHOG_BOOLEAN_FLAGS)[number];

export const usePostHogFeatureFlag = (
  flagKey: PostHogFeatureFlagKey,
): { value: boolean; loading: boolean } => {
  const enabled = useFeatureFlagEnabled(flagKey);
  useEffect(() => {
    warnOnFlagReadUnderForeignIdentity(flagKey);
  }, [flagKey, enabled]);
  return { value: enabled ?? false, loading: enabled === undefined };
};

export interface ControlFlagSpec {
  readonly arms: readonly [string, ...string[]];
  readonly family?: true;
}

export const CONTROL_FLAGS = {
  // Gates the reading-sequence pilot.
  control_block_lesson_feat: { arms: ['block-lesson-enabled'] },
  // Gates « Le résumé de ton cours » (Lesson digest) on Mon cours; generation is never gated.
  control_lesson_digest_feat: { arms: ['lesson-digest'] },
  control_home_tutor_feat: { arms: ['test'], family: true },
  // Android in-app web camera: every arm after control opens it, with the camera profile src/modules/camera/profiles.ts maps to the arm.
  control_web_camera_feat: { arms: ['test', 'no-still', 'worker-scorer'] },
  control_onboarding_three_month_goals_feat: { arms: ['test'] },
  control_onboarding_labor_effect_feat: { arms: ['test'] },
} as const satisfies Record<`control_${string}_feat`, ControlFlagSpec>;

export type ControlFeatureFlagKey = keyof typeof CONTROL_FLAGS;

export type ReportedFlagKey = PostHogFeatureFlagKey | ControlFeatureFlagKey;

export const REPORTED_FLAG_KEYS: readonly ReportedFlagKey[] = [
  ...POSTHOG_BOOLEAN_FLAGS,
  ...(Object.keys(CONTROL_FLAGS) as ControlFeatureFlagKey[]),
];

export const isControlFlagKey = (key: string): key is ControlFeatureFlagKey =>
  hasOwn(CONTROL_FLAGS, key);

export const isFamilyBucketed = (key: ControlFeatureFlagKey): boolean =>
  (CONTROL_FLAGS[key] as ControlFlagSpec).family === true;

export type ControlFlagVariant<K extends ControlFeatureFlagKey> =
  'control' | (typeof CONTROL_FLAGS)[K]['arms'][number];

export interface ControlFlagRollout<K extends ControlFeatureFlagKey> {
  variant: ControlFlagVariant<K> | null;
  enabled: boolean;
  loading: boolean;
}

// The arm a raw PostHog value means, for an already-answered flag.
export const controlFlagArm = <K extends ControlFeatureFlagKey>(
  flagKey: K,
  raw: string | boolean,
): ControlFlagVariant<K> => {
  if (typeof raw === 'boolean') {
    return raw ? CONTROL_FLAGS[flagKey].arms[0] : 'control';
  }
  const enablingArms = CONTROL_FLAGS[flagKey].arms as readonly string[];
  return enablingArms.includes(raw)
    ? (raw as ControlFlagVariant<K>)
    : 'control';
};

export const isEnablingArm = <K extends ControlFeatureFlagKey>(
  variant: ControlFlagVariant<K>,
): boolean => variant !== 'control';

export const useControlFeatureFlag = <K extends ControlFeatureFlagKey>(
  flagKey: K,
): ControlFlagRollout<K> => {
  const raw = useFeatureFlagVariantKey(flagKey);
  const familyReady = useFamilyGroupPropertiesReady();
  useEffect(() => {
    warnOnFlagReadUnderForeignIdentity(flagKey);
  }, [flagKey, raw]);

  if (raw === undefined || (isFamilyBucketed(flagKey) && !familyReady)) {
    return { variant: null, enabled: false, loading: true };
  }
  const variant = controlFlagArm(flagKey, raw);
  return { variant, enabled: isEnablingArm(variant), loading: false };
};
